import { Component, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime, map, switchMap } from 'rxjs';
import { ICohort, IProfile, IResponse, IUser } from '@interfaces/index';
import { MatDialog } from '@angular/material/dialog';
import { CohortService } from '../../services/cohort.service';
import { AppService } from '@ui-components/services';
import { UserService } from '../../services/user.service';
import { AddUserModalComponent } from '../add-cohort-user-modal/add-cohort-user-modal.component';
import { CohortModalComponent } from '../cohort-modal/cohort-modal.component';
import { ICreate } from 'projects/ui-components/src/interfaces/ICohort';

@Component({
  selector: 'app-cohorts',
  templateUrl: './cohorts.component.html',
  styleUrl: './cohorts.component.scss'
})
export class CohortsComponent {

  columns = ['name', 'email', 'org', 'remove'];
  form: FormGroup;
  loading = false;
  profile: IProfile | null = null;
  filteredCohorts: ICohort[] = [];
  cohorts: ICohort[] = [];
  users: IUser[] = [];
  private destroyRef: DestroyRef;

  constructor(
    protected _app_service: AppService,
    protected _user_service: UserService,
    protected _cohort_service: CohortService,
    protected _fb: FormBuilder,
    public dialog: MatDialog,
    destroyRef: DestroyRef
  ) {
    this.form = this._fb.group({
      search: [null]
    });
    this.destroyRef = destroyRef;
  }

  ngOnInit(): void {
    this._app_service.selectedManagementGroup$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.loadCohorts();
    });

    this._app_service.getProfile().pipe(
      switchMap(
        (response: IResponse<IProfile>) => {
          this.profile = response.data;
          return this._cohort_service.getAll(this.profile.org_id);
        }
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      (response: IResponse<ICohort[]>) => {
        this.cohorts = response.data;
        this.filteredCohorts = this.cohorts;
        this.searchByName();
      }
    );
  }

  loadCohorts(): void {
    if (this.profile) {
      this.loading = true;
      this._cohort_service.getAll(this.profile.org_id).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(
        (response: IResponse<ICohort[]>) => {
          this.cohorts = response.data;
          this.filteredCohorts = this.cohorts;
          this.loading = false;
        }
      );
    }
  }

  searchByName(): void {
    this.form.valueChanges.pipe(
      debounceTime(800),
      map(() => {
        const search = this.form.get('search')?.value === '' ? null : this.form.get('search')?.value;
        if (search) {
          return this.cohorts.filter(cohort => cohort.name.toLowerCase().includes(search.toLowerCase()));
        } else {
          return this.cohorts;
        }
      })
    ).subscribe(filteredCohorts => {
      this.filteredCohorts = filteredCohorts;
    });
  }

  getCohortTitle(cohort: ICohort): string {
    return `${cohort.isExpanded ? '-' : '+'} ${cohort.name}`;
  }

  toggleCohort(cohort: ICohort): void {
    cohort.isExpanded = !cohort.isExpanded;
  }

  remove(cohort_id: number, user_id: number): void {
    this._cohort_service.removeUser(cohort_id, user_id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.loadCohorts();
    });
  }

  createCohortModal(name: ICreate | null = null): void {
    const dialog_ref = this.dialog.open(CohortModalComponent, {
      width: '600px',
      data: name
    });

    dialog_ref.afterClosed().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((name: ICreate | null) => {
      if (name) {
        this.loadCohorts();
      }
    });
  }

  addUserModal(cohort_id: number): void {
    const dialog_ref = this.dialog.open(AddUserModalComponent, {
      width: '600px',
      data: cohort_id
    });

    dialog_ref.afterClosed().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((reload: boolean) => {
      if (reload) {
        this.loadCohorts();
      }
    });
  }

  clearSearch(): void {
    this.form.reset();
    this.loadCohorts();
  }
}
